import apiClient from './api'

export interface FraudRisk {
  id: string
  transactionId: string
  type: 'deposit' | 'withdrawal'
  msisdn: string
  amount: number
  riskScore: number
  riskLevel: 'low' | 'medium' | 'high' | 'critical'
  flags: string[]
  reason: string
  detectedAt?: string
  timestamp?: string
  triggeredRules?: string[]
  actionTaken?: string
}

export interface FraudRule {
  id: string
  name: string
  description: string
  enabled: boolean
  ruleType: 'amount' | 'frequency' | 'volume' | 'time' | 'status' | 'custom'
  conditionParams: Record<string, any>
  riskScore: number
  action: 'flag' | 'block' | 'alert'
  priority: number
  scheduleInterval?: number  // Time interval in hours (0 = no scheduled execution)
  createdAt?: string
  updatedAt?: string
}

export const fraudService = {
  // Get fraud risks
  getFraudRisks: async (filters?: { level?: string; msisdn?: string }): Promise<FraudRisk[]> => {
    try {
      const params: Record<string, any> = {}
      if (filters?.level) params.level = filters.level
      if (filters?.msisdn) params.msisdn = filters.msisdn

      const response = await apiClient.get('/fraud/risks', { params })
      return response.data.risks || response.data || []
    } catch (error) {
      console.error('Failed to fetch fraud risks', error)
      return []
    }
  },

  // Get fraud rules
  getFraudRules: async (): Promise<FraudRule[]> => {
    try {
      const response = await apiClient.get('/fraud/rules')
      return response.data.rules || response.data || []
    } catch (error) {
      console.error('Failed to fetch fraud rules', error)
      return []
    }
  },

  // Create fraud rule
  createFraudRule: async (rule: Omit<FraudRule, 'id' | 'createdAt' | 'updatedAt'>): Promise<FraudRule> => {
    const response = await apiClient.post('/fraud/rules', rule)
    return response.data
  },

  // Get fraud rule by ID
  getFraudRule: async (ruleId: string): Promise<FraudRule> => {
    const response = await apiClient.get(`/fraud/rules/${ruleId}`)
    return response.data
  },

  // Update fraud rule
  updateFraudRule: async (ruleId: string, updates: Partial<FraudRule>): Promise<FraudRule> => {
    const response = await apiClient.patch(`/fraud/rules/${ruleId}`, updates)
    return response.data
  },

  // Delete fraud rule
  deleteFraudRule: async (ruleId: string): Promise<void> => {
    await apiClient.delete(`/fraud/rules/${ruleId}`)
  },

  // Test fraud rule
  testFraudRule: async (ruleId: string): Promise<{
    success: boolean
    message: string
    ruleMatched: boolean
    testTransaction?: any
    ruleEvaluation?: {
      matched: boolean
      flag: string
      riskScore: number
      riskLevel: string
      action: string
    }
    email?: {
      sent: boolean
      preview?: {
        to: string[]
        subject: string
        body: string
      }
      error?: string
    }
  }> => {
    const response = await apiClient.post(`/fraud/test-rule/${ruleId}`)
    return response.data
  },

  // Add to blacklist
  addToBlacklist: async (msisdn: string, reason: string): Promise<void> => {
    await apiClient.post('/fraud/blacklist', { msisdn, reason })
  },

  // Remove from blacklist
  removeFromBlacklist: async (msisdn: string): Promise<void> => {
    await apiClient.delete(`/fraud/blacklist/${msisdn}`)
  },

  // Get blacklist
  getBlacklist: async (): Promise<Array<{ msisdn: string; reason: string; addedAt: string }>> => {
    try {
      const response = await apiClient.get('/fraud/blacklist')
      return response.data.blacklist || response.data || []
    } catch (error) {
      console.error('Failed to fetch blacklist', error)
      return []
    }
  },

  // Notification emails
  getNotificationEmails: async (): Promise<FraudNotificationEmail[]> => {
    try {
      const response = await apiClient.get('/fraud/notification-emails')
      return response.data.emails || response.data || []
    } catch (error) {
      console.error('Failed to fetch notification emails', error)
      return []
    }
  },

  createNotificationEmail: async (email: Omit<FraudNotificationEmail, 'id' | 'createdAt' | 'updatedAt'>): Promise<FraudNotificationEmail> => {
    const response = await apiClient.post('/fraud/notification-emails', email)
    return response.data
  },

  updateNotificationEmail: async (emailId: string, updates: Partial<FraudNotificationEmail>): Promise<FraudNotificationEmail> => {
    const response = await apiClient.patch(`/fraud/notification-emails/${emailId}`, updates)
    return response.data
  },

  deleteNotificationEmail: async (emailId: string): Promise<void> => {
    await apiClient.delete(`/fraud/notification-emails/${emailId}`)
  },
}

export interface FraudNotificationEmail {
  id: string
  email: string
  enabled: boolean
  notifyOnCritical: boolean
  notifyOnHigh: boolean
  notifyOnMedium: boolean
  notifyOnLow: boolean
  createdAt?: string
  updatedAt?: string
}


